/* eslint-disable jsx-a11y/no-static-element-interactions */
import { CenterPivot } from "protos/farm/farm";
import { classes } from "portal/theme/theme";
import { DateTime } from "luxon";
import { getLastCheckedString, getLastUpdatedString } from "portal/utils/dates";
import { jobExplorer } from "common/state/jobExplorer";
import { LinearProgress, Typography } from "@mui/material";
import {
  PIVOT_POLL_INTERVAL_S,
  PIVOT_STALE_THRESHOLD_S,
} from "common/utils/rtcJobs";
import { RootState } from "portal/state/store";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import React, { FC, useEffect, useState } from "react";

interface CenterPivotDetailsProps {
  centerPivot: CenterPivot;
}

const STATIC_POLL_THRESHOLD_S = PIVOT_POLL_INTERVAL_S + 1;
const LAST_UPDATED_THRESHOLD = PIVOT_STALE_THRESHOLD_S;
const STATIC_POLL_UPPER_THRESHOLD_M = 10; // 10 minutes
const LAST_UPDATED_UPPER_THRESHOLD_M = 10; // 10 minutes

export const CenterPivotDetails: FC<CenterPivotDetailsProps> = ({
  centerPivot,
}) => {
  const { t } = useTranslation();

  const deviceData = useSelector((state: RootState) =>
    jobExplorer.selectors.selectEndpointDeviceDataByDeviceIds(state, [
      centerPivot.endpointDeviceId,
    ])
  )[centerPivot.endpointDeviceId];
  const [now, setNow] = useState<DateTime>(DateTime.now());
  useEffect(() => {
    const interval = setInterval(() => {
      setNow(DateTime.now());
    }, 1000);
    return () => {
      clearInterval(interval);
    };
  }, []);

  const lastCheckedSeconds = deviceData?.liveDeviceData?.timestamp
    ? now.toSeconds() -
      DateTime.fromISO(deviceData.liveDeviceData.timestamp).toSeconds()
    : undefined;
  const lastCheckedString = getLastCheckedString({
    lastCheckedSeconds,
    staticPollThreshold: STATIC_POLL_THRESHOLD_S,
    staticPollUpperThreshold: STATIC_POLL_UPPER_THRESHOLD_M,
    t,
  });
  const lastUpdatedSeconds = deviceData?.liveDeviceData?.timestamp
    ? now.toSeconds() -
      DateTime.fromISO(deviceData.liveDeviceData.timestamp).toSeconds()
    : Number.POSITIVE_INFINITY;

  const lastUpdatedString = getLastUpdatedString({
    lastUpdatedSeconds,
    lastUpdatedThreshold: LAST_UPDATED_THRESHOLD,
    lastUpdatedUpperThreshold: LAST_UPDATED_UPPER_THRESHOLD_M,
    t,
  });

  return (
    <div>
      <LinearProgress
        variant={deviceData?.deviceIsMoving ? "indeterminate" : "determinate"}
        value={100}
        color={
          // eslint-disable-next-line no-nested-ternary
          deviceData?.deviceIsMoving
            ? "warning"
            : deviceData?.deviceIsStale
            ? "secondary"
            : "success"
        }
      />
      <div className="flex flex-col p-4 gap-1">
        <div className="flex justify-between">
          <Typography
            variant="h6"
            className={classes(
              "flex my-auto",
              deviceData?.deviceIsStale ? "text-slate-500" : "text-green-500"
            )}
          >
            {deviceData?.deviceIsStale
              ? t("utils.descriptors.inactive")
              : t("utils.descriptors.active")}
          </Typography>
          <Typography className="flex my-auto">
            {`${new Date(
              deviceData?.liveDeviceData?.timestamp ?? ""
            ).toLocaleDateString()}
            ${new Date(
              deviceData?.liveDeviceData?.timestamp ?? ""
            ).toLocaleTimeString()}`}
          </Typography>
        </div>
        <Typography className="text-slate-500 text-sm flex my-auto justify-end">
          {lastUpdatedString}
        </Typography>
        <Typography className="text-slate-500 text-sm flex my-auto justify-end">
          {lastCheckedString}
        </Typography>
      </div>
    </div>
  );
};
